export const COMPLIANCE_QUESTIONNAIRES = {
  WA_AFH_072_FIRE_EXTINGUISHERS: {
    label: "Fire Extinguishers",
    reference: "WAC 388-76-10720, WAC 388-76-10725",
    description:
      "Confirm extinguisher coverage, rating, placement, and monthly/annual inspection status.",
    icon: "ti-fire-extinguisher",
    questions: [
      {
        key: "everyFloorHasOne",
        prompt: "Does every floor have a fire extinguisher?",
        type: "boolean",
        required: true,
      },
      {
        key: "meetsMinimumRating",
        prompt:
          "Do all extinguishers meet the minimum 2A:10B:C or 5 lb ABC rating?",
        type: "boolean",
        required: true,
      },
      {
        key: "properlyMounted",
        prompt:
          "Are all extinguishers wall-mounted, visible, and unobstructed?",
        type: "boolean",
        required: true,
      },
      {
        key: "coversKitchen",
        prompt: "Is there an extinguisher near the kitchen/cooking area?",
        type: "boolean",
        required: true,
      },
      {
        key: "kitchenDistanceFeet",
        prompt: "How far is it from the cooking equipment (feet)?",
        type: "number",
        showIf: { key: "coversKitchen", equals: true },
        required: true,
      },
      {
        key: "monthlyCheckDate",
        prompt: "When was the last monthly visual check?",
        type: "date",
        required: true,
      },
      {
        key: "annualInspectionDate",
        prompt: "When was the last annual professional inspection?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_076_DOORS_ABILITY_TO_OPEN: {
    label: "Doors",
    reference: "WAC 388-76-10715",
    description:
      "Track door hardware, locking mechanisms, accessibility, and width compliance.",
    icon: "ti-door",
    questions: [
      {
        key: "bedroomBathroomDoorsOpenFreely",
        prompt:
          "Do all bedroom and bathroom doors open freely from both inside and outside?",
        type: "boolean",
        required: true,
      },
      {
        key: "locksUnlockableFromOutside",
        prompt:
          "Can all locked bedroom/bathroom doors be unlocked from outside without a key or tool in an emergency?",
        type: "boolean",
        required: true,
      },
      {
        key: "closetDoorsOpenBothSides",
        prompt: "Do all closet doors open from both inside and outside?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasOneDesignatedExit",
        prompt:
          "Is exactly one exterior door designated as the emergency exit?",
        type: "boolean",
        required: true,
      },
      {
        key: "exitHasLeverHandlesAndReentry",
        prompt:
          "Does that exit have lever handles on both sides and allow exit/re-entry without a key?",
        type: "boolean",
        required: true,
      },
      {
        key: "otherExteriorDoorsAccessible",
        prompt:
          "Are all other exterior doors freely accessible, or is a documented safety exemption on file for any that aren't?",
        type: "boolean",
        required: true,
      },
      {
        key: "allDoorsMeetWidthMinimum",
        prompt: "Do all resident-used doors meet the minimum 27-inch width?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_077_WINDOWS: {
    label: "Windows",
    reference: "WAC 388-76-10795",
    description:
      "Confirm bedroom windows meet sill height, opening size, and emergency egress requirements.",
    icon: "ti-window",
    questions: [
      {
        key: "everyBedroomHasQualifyingWindow",
        prompt:
          "Does every bedroom have at least one window meeting sill height (\u226444in), opening size (\u22655.7 sq ft), and opening dimension requirements?",
        type: "boolean",
        required: true,
      },
      {
        key: "windowsOpenableWithoutTools",
        prompt:
          "Can all qualifying windows be opened from inside without keys, tools, or special effort?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasStormWindows",
        prompt: "Are any bedroom windows fitted with storm windows?",
        type: "boolean",
        required: true,
      },
      {
        key: "stormWindowsHaveReleaseMechanism",
        prompt:
          "Do those storm windows have a release mechanism that preserves emergency egress?",
        type: "boolean",
        showIf: { key: "hasStormWindows", equals: true },
        required: true,
      },
      {
        key: "hasBasementWindows",
        prompt: "Does the home have any basement bedroom windows?",
        type: "boolean",
        required: true,
      },
      {
        key: "basementWindowsUnobstructed",
        prompt:
          "Are those basement windows free from obstructions blocking emergency escape or rescue?",
        type: "boolean",
        showIf: { key: "hasBasementWindows", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_071_SMOKE_ALARMS: {
    label: "Smoke Alarms",
    reference: "WAC 388-76-10805",
    description:
      "Confirm smoke alarm coverage, interconnection, working condition, and testing/battery schedule throughout the home.",
    icon: "ti-alarm",
    questions: [
      {
        key: "everyBedroomHasAlarm",
        prompt: "Does every resident bedroom have a smoke alarm?",
        type: "boolean",
        required: true,
      },
      {
        key: "everyFloorHasAlarm",
        prompt: "Does every floor level have smoke alarm coverage?",
        type: "boolean",
        required: true,
      },
      {
        key: "alarmsInterconnectedOrWireless",
        prompt:
          "Are all alarms interconnected (or wireless with all sounding together) so one activating triggers all?",
        type: "boolean",
        required: true,
      },
      {
        key: "allAlarmsWorking",
        prompt: "Are all smoke alarms currently in working condition?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastOperationalTestDate",
        prompt:
          "When was the last monthly operational test (audible alert/test button) performed on all alarms?",
        type: "date",
        required: true,
      },
      {
        key: "usesSealed10YearBatteries",
        prompt:
          "Are all smoke alarms sealed, non-removable 10-year battery units?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastBatteryReplacementDate",
        prompt: "When were batteries last replaced?",
        type: "date",
        showIf: { key: "usesSealed10YearBatteries", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_080_SPACE_HEATERS_FIREPLACES_STOVES: {
    label: "Space Heaters, Fireplaces & Stoves",
    reference: "WAC 388-76-10825",
    description:
      "Confirm space heater certification, escape-route clearance, and barriers on hot-to-touch surfaces.",
    icon: "ti-flame",
    questions: [
      {
        key: "hasSpaceHeaters",
        prompt:
          "Does the home use any oil, gas, kerosene, or electric space heaters?",
        type: "boolean",
        required: true,
      },
      {
        key: "heatersAreLabCertified",
        prompt:
          "Are those space heaters certified by a nationally recognized testing laboratory?",
        type: "boolean",
        showIf: { key: "hasSpaceHeaters", equals: true },
        required: true,
      },
      {
        key: "dontBlockEscapeRoutes",
        prompt:
          "Do all stoves and heaters stay clear of resident, staff, and household escape routes?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasHotToTouchAppliances",
        prompt:
          "Do any fireplaces, stoves, or heaters get hot to the touch when in use?",
        type: "boolean",
        required: true,
      },
      {
        key: "hotSurfacesHaveBarriers",
        prompt:
          "Do those hot-to-touch surfaces have a stable, flame-resistant barrier that stays cool and prevents contact?",
        type: "boolean",
        showIf: { key: "hasHotToTouchAppliances", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_078_BEDROOMS_USABLE_SPACE: {
    label: "Bedrooms Usable Space",
    reference: "WAC 388-76-10685, WAC 388-76-10690",
    description:
      "Confirm bedroom square footage, storage, light, furnishings, privacy, and room-sharing compliance.",
    icon: "ti-bed",
    questions: [
      {
        key: "allMeetSquareFootage",
        prompt:
          "Do all bedrooms meet the required usable space (80 sq ft single / 120 sq ft shared, excluding closets)?",
        type: "boolean",
        required: true,
      },
      {
        key: "allHaveClosets",
        prompt: "Does every bedroom have an accessible closet or wardrobe?",
        type: "boolean",
        required: true,
      },
      {
        key: "allHaveNaturalLight",
        prompt: "Is every bedroom an outside room with direct natural light?",
        type: "boolean",
        required: true,
      },
      {
        key: "bedsCompliant",
        prompt:
          "Are all beds at least 36 inches wide with clean bedding, and no upper bunks in use?",
        type: "boolean",
        required: true,
      },
      {
        key: "locksOfferedOrExempted",
        prompt:
          "Is a lock offered on every bedroom door, or is a documented safety exception on file for any that don't?",
        type: "boolean",
        required: true,
      },
      {
        key: "noMinorSharingWithNonRelative",
        prompt:
          "If any resident under 18 shares a bedroom, is that roommate their biological or legal parent/guardian — with no unrelated resident, staff, or household member sharing that room?",
        type: "boolean",
        required: true,
      },
      {
        key: "allDshsApproved",
        prompt:
          "Are all bedrooms currently DSHS-approved for resident placement?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_001_LICENSE_STATUS_AND_CONDITIONS: {
    label: "License Status and Conditions",
    reference: "WAC 388-76-10015; Ch. 70.128 RCW",
    description:
      "Confirm the home's AFH license is active and operating within its approved conditions.",
    icon: "ti-certificate",
    questions: [
      {
        key: "licenseActive",
        prompt:
          "Is the home's AFH license currently active and in good standing?",
        type: "boolean",
        required: true,
      },
      {
        key: "withinLicenseConditions",
        prompt:
          "Is the home operating fully within its approved license conditions (capacity, specialty designations, etc.)?",
        type: "boolean",
        required: true,
      },
      {
        key: "licenseExpirationDate",
        prompt: "When does the current license expire?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_004_LIABILITY_INSURANCE: {
    label: "Liability Insurance",
    reference: "WAC 388-76-10191; WAC 388-76-10192",
    description: "Confirm required liability coverage is active with no lapse.",
    icon: "ti-shield-check",
    questions: [
      {
        key: "hasActiveCoverage",
        prompt:
          "Is required liability insurance currently active with no lapse in coverage?",
        type: "boolean",
        required: true,
      },
      {
        key: "meetsRequiredLimits",
        prompt: "Does the policy meet the required coverage limits?",
        type: "boolean",
        required: true,
      },
      {
        key: "policyExpirationDate",
        prompt: "When does the current policy expire?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_023_SUCCESSION_PLAN: {
    label: "Succession Plan",
    reference: "WAC 388-76-10201",
    description:
      "Confirm a current succession plan is on file for continuity of home operations.",
    icon: "ti-replace",
    questions: [
      {
        key: "hasCurrentPlan",
        prompt: "Does the home have a current written succession plan?",
        type: "boolean",
        required: true,
      },
      {
        key: "reviewedRecently",
        prompt:
          "Has the plan been reviewed or updated within the last year, or since the last material change?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_024_MEDICAID_STATE_FUNDED_RESIDENT_OBLIGATIONS: {
    label: "Medicaid / State-Funded Resident Obligations",
    reference: "WAC 388-76-10205; WAC 388-76-10522",
    description:
      "Confirm required policies and notices for state-funded residents are current and applied.",
    icon: "ti-file-dollar",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written policy on accepting Medicaid/state-funded residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "noticesProvided",
        prompt:
          "Have all applicable residents/representatives received the required notice?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_025_RESIDENT_FUNDS_PROTECTION: {
    label: "Resident Funds Protection",
    reference: "WAC 388-76-10215",
    description:
      "Confirm resident funds are protected with complete accounting and authorization records.",
    icon: "ti-cash",
    questions: [
      {
        key: "fundsProtected",
        prompt:
          "Are resident funds held/managed by the home protected according to policy?",
        type: "boolean",
        required: true,
      },
      {
        key: "recordsComplete",
        prompt:
          "Are accounting and authorization records for resident funds complete and up to date?",
        type: "boolean",
        required: true,
      },
      {
        key: "noUnexplainedDiscrepancies",
        prompt:
          "Are there zero unexplained discrepancies in resident fund accounts?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_040_RESIDENT_RECORD_AVAILABILITY_CONFIDENTIALITY: {
    label: "Resident Record Availability & Confidentiality",
    reference: "WAC 388-76-10315",
    description:
      "Confirm resident records are protected from unauthorized access while remaining available to authorized reviewers.",
    icon: "ti-clipboard-text",
    questions: [
      {
        key: "recordsSecured",
        prompt: "Are resident records protected from unauthorized access?",
        type: "boolean",
        required: true,
      },
      {
        key: "availableToAuthorizedStaff",
        prompt:
          "Are resident records readily available to authorized department staff upon request?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_041_RESIDENT_RECORD_RETENTION: {
    label: "Resident Record Retention",
    reference: "WAC 388-76-10315",
    description:
      "Confirm resident records are retained for the applicable regulatory period.",
    icon: "ti-archive",
    questions: [
      {
        key: "retentionPolicyFollowed",
        prompt:
          "Are resident records retained for the required regulatory retention period, including after discharge?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_052_MEDICATION_STORAGE_AND_SECURITY: {
    label: "Medication Storage and Security",
    reference: "Ch. 388-76 WAC — medication storage requirements",
    description:
      "Confirm medications are stored securely and identified according to requirements.",
    icon: "ti-lock",
    questions: [
      {
        key: "storedSecurely",
        prompt:
          "Are all medications stored securely with no unauthorized access?",
        type: "boolean",
        required: true,
      },
      {
        key: "storedPerInstructions",
        prompt:
          "Are medications stored according to labeling/manufacturer instructions (e.g., refrigeration where required)?",
        type: "boolean",
        required: true,
      },
      {
        key: "properlyIdentified",
        prompt:
          "Are all medications properly labeled and identified for the correct resident?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_058_RESTRAINTS_INVOLUNTARY_SECLUSION: {
    label: "Restraints / Involuntary Seclusion",
    reference: "Ch. 388-76 WAC — restraints/seclusion",
    description:
      "Confirm no prohibited restraint or seclusion practices are in use.",
    icon: "ti-hand-stop",
    questions: [
      {
        key: "noProhibitedRestraints",
        prompt:
          "Are physical or mechanical restraints being used only in permitted, documented circumstances (never as a prohibited restraint)?",
        type: "boolean",
        required: true,
      },
      {
        key: "noChemicalRestraints",
        prompt:
          "Is medication used only for legitimate clinical purposes, never as a chemical restraint?",
        type: "boolean",
        required: true,
      },
      {
        key: "noInvoluntarySeclusion",
        prompt: "Is the home free of any involuntary seclusion practices?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_060_RESIDENT_RIGHTS_IMPLEMENTATION: {
    label: "Resident Rights Implementation",
    reference: "Ch. 388-76 WAC; Ch. 70.129 RCW",
    description:
      "Confirm resident rights are respected in daily operations, not just posted.",
    icon: "ti-users",
    questions: [
      {
        key: "dignityAndPrivacyRespected",
        prompt:
          "Are resident dignity and privacy respected in daily care and living arrangements?",
        type: "boolean",
        required: true,
      },
      {
        key: "participationAndCommunicationSupported",
        prompt:
          "Are residents supported in participation, communication, and receiving visitors as they choose?",
        type: "boolean",
        required: true,
      },
      {
        key: "noUnresolvedViolations",
        prompt:
          "Are there zero unresolved, substantiated resident rights violations?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_061_MEDICAID_PAYMENT_SOURCE_NOTICE_POLICY: {
    label: "Medicaid Payment-Source Notice / Policy",
    reference: "WAC 388-76-10522",
    description:
      "Confirm the Medicaid payment-source policy is current and provided to applicable residents.",
    icon: "ti-file-invoice",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written policy on accepting Medicaid as a payment source?",
        type: "boolean",
        required: true,
      },
      {
        key: "noticeProvidedToApplicable",
        prompt:
          "Have all applicable residents/representatives received the required notice of this policy?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_065_EMERGENCY_DISASTER_PLAN: {
    label: "Emergency / Disaster Plan",
    reference: "Ch. 388-76 WAC — emergency/disaster planning",
    description:
      "Confirm a current emergency/disaster plan is on file and staff are trained on it.",
    icon: "ti-alert-triangle",
    questions: [
      {
        key: "hasCurrentPlan",
        prompt:
          "Does the home have a current written emergency/disaster plan addressing resident needs and staff actions?",
        type: "boolean",
        required: true,
      },
      {
        key: "staffTrainedOnHire",
        prompt: "Are all staff trained on the plan when they begin work?",
        type: "boolean",
        required: true,
      },
      {
        key: "planReviewedRecently",
        prompt: "Has the plan been reviewed within the required timeframe?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_073_EVACUATION_ROUTES_EXITS: {
    label: "Evacuation Routes / Exits",
    reference: "Ch. 388-76 WAC — emergency/fire safety/doors",
    description:
      "Confirm designated evacuation routes and exits stay clear and usable at all times.",
    icon: "ti-door-exit",
    questions: [
      {
        key: "routesUnobstructed",
        prompt: "Are all designated evacuation routes free of obstructions?",
        type: "boolean",
        required: true,
      },
      {
        key: "exitsUsable",
        prompt:
          "Are all required exits usable and operable without special tools or knowledge?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_074_SAFETY_AND_MAINTENANCE: {
    label: "Safety and Maintenance",
    reference: "WAC 388-76-10750",
    description:
      "Confirm the physical plant, grounds, equipment, hazardous materials, and pest control are safe and well-maintained.",
    icon: "ti-tools",
    questions: [
      {
        key: "houseCleanAndSystemsOperable",
        prompt:
          "Are buildings, fixtures, and systems (heating, plumbing, electrical) clean, sanitary, operable, and in good repair?",
        type: "boolean",
        required: true,
      },
      {
        key: "groundsFreeOfHazards",
        prompt:
          "Are walkways, driveways, patios, and yards clear of trip/fall hazards, snow/ice, debris, or overgrowth?",
        type: "boolean",
        required: true,
      },
      {
        key: "equipmentAndFurnitureSafe",
        prompt:
          "Are resident furniture, appliances, mobility equipment, and mechanical systems structurally sound, stable, and in safe working order?",
        type: "boolean",
        required: true,
      },
      {
        key: "hazardousMaterialsSecured",
        prompt:
          "Are toxic substances, cleaning chemicals, insecticides, and flammable liquids stored safely and securely (locked where required by resident care assessments)?",
        type: "boolean",
        required: true,
      },
      {
        key: "garbageAndPestControlled",
        prompt:
          "Is trash stored in covered containers and removed regularly, with effective pest control measures in place?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasUnresolvedHazard",
        prompt:
          "Is there a known physical safety hazard that hasn't been repaired yet?",
        type: "boolean",
        required: true,
      },
      {
        key: "hazardDetails",
        prompt:
          "Briefly describe the hazard and what's been done so far (required immediately under WAC — repairs must be initiated upon discovery).",
        type: "text",
        showIf: { key: "hasUnresolvedHazard", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_079_COMMON_USE_AREAS: {
    label: "Common-Use Areas",
    reference: "Ch. 388-76 WAC — common-use areas",
    description: "Confirm common-use areas are adequate, safe, and accessible.",
    icon: "ti-sofa",
    questions: [
      {
        key: "adequateSpaceProvided",
        prompt:
          "Does the home provide adequate common-use space for residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "safeAndAccessible",
        prompt:
          "Are common-use areas maintained safely and accessible to all residents?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_082_EMERGENCY_FOOD_SUPPLY: {
    label: "Emergency Food Supply",
    reference: "Ch. 388-76 WAC — emergency food supply",
    description:
      "Confirm the home maintains at least 72 hours of on-site emergency food.",
    icon: "ti-shopping-cart",
    questions: [
      {
        key: "hasSeventyTwoHourSupply",
        prompt:
          "Does the home currently have at least 72 hours of on-site emergency food supply for residents and household members?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastInventoryCheckDate",
        prompt: "When was the emergency food supply last checked/rotated?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_053_MEDICATION_DISPOSAL_POLICY: {
    label: "Medication Disposal Policy",
    reference: "WAC 388-76-10490",
    description:
      "Confirm a current written medication disposal policy is in place and followed.",
    icon: "ti-trash-x",
    questions: [
      {
        key: "hasCurrentPolicy",
        prompt:
          "Does the home have a current written medication disposal policy?",
        type: "boolean",
        required: true,
      },
      {
        key: "disposalsDocumented",
        prompt:
          "Are medication disposal events documented as required by that policy?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_083_INFECTION_CONTROL_COMMUNICABLE_DISEASE: {
    label: "Infection Control / Communicable Disease",
    reference: "WAC 388-76-10255; WAC 388-76-10225(3)",
    description:
      "Confirm the home has an infection control system that follows nationally recognized standards and safety-act requirements, and that outbreaks were reported to public health and the department.",
    icon: "ti-virus",
    questions: [
      {
        key: "infectionControlSystemInPlace",
        prompt:
          "Has the home developed and implemented an infection control system that uses nationally recognized infection control standards?",
        type: "boolean",
        required: true,
      },
      {
        key: "infectionControlPracticesFollowed",
        prompt:
          "Are standard infection-control practices (frequent hand washing and other ways of limiting the spread of infection) consistently followed?",
        type: "boolean",
        required: true,
      },
      {
        key: "followsWishaRequirements",
        prompt:
          "Does the home follow the Washington Industrial Safety and Health Act requirements that apply to protect residents and employees (for example, a written respiratory protection program where one is required)?",
        type: "boolean",
        required: true,
      },
      {
        key: "sharpsDisposedSafely",
        prompt:
          "Do staff dispose of razor blades, syringes, and other sharp items in a way that does not risk the health and safety of residents, staff, other people in the home, or the public?",
        type: "boolean",
        required: true,
      },
      {
        key: "singleUseSuppliesUsedOnce",
        prompt:
          "Do staff use disposable and single-service supplies and equipment only once, as the manufacturer specifies?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasOutbreakEvent",
        prompt:
          "Has the home had an outbreak of suspected food poisoning or communicable disease since the last check?",
        type: "boolean",
        required: true,
      },
      {
        key: "outbreakReportedToPublicHealth",
        prompt:
          "Was the local public health officer notified of that outbreak?",
        type: "boolean",
        showIf: { key: "hasOutbreakEvent", equals: true },
        required: true,
      },
      {
        key: "outbreakReportedToDepartment",
        prompt:
          "Was the department's complaint hotline or online reporting system notified of that outbreak?",
        type: "boolean",
        showIf: { key: "hasOutbreakEvent", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_089_REQUIRED_POLICIES_CURRENT: {
    label: "Required Policies Current",
    reference:
      "WAC 388-76-10675; WAC 388-76-10830; WAC 388-76-10490; WAC 388-76-10522; other provisions of Ch. 388-76 WAC",
    description:
      "Confirm the required written policies and plans are on file and current, including abuse and reporting, emergency and disaster, medication disposal, and Medicaid payment-source policies.",
    icon: "ti-file-check",
    questions: [
      {
        key: "hasAbuseReportingPolicies",
        prompt:
          "Does the home have written rules and policies that prohibit abandonment, abuse, neglect, and exploitation, require staff to report possible abuse, and do not interfere with staff or mandated reporters reporting directly to the department and law enforcement?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasEmergencyDisasterPlan",
        prompt:
          "Does the home have a written emergency and disaster plan that covers responding to natural and man-made emergencies, staff and resident actions during and after, and the fire drill plan for evacuation?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasMedicationDisposalPolicy",
        prompt: "Does the home have a written medication disposal policy?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasMedicaidPaymentPolicy",
        prompt:
          "Does the home have a written policy on accepting Medicaid or other public funds as a payment source?",
        type: "boolean",
        required: true,
      },
      {
        key: "allRequiredPoliciesPresent",
        prompt:
          "Does the home have every other written policy or plan required under chapter 388-76 WAC currently on file?",
        type: "boolean",
        required: true,
      },
      {
        key: "allPoliciesCurrent",
        prompt:
          "Have all of the home's written policies been reviewed and updated to reflect current operations and rule changes, with review or effective dates up to date?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasKnownGap",
        prompt: "Is any required policy known to be missing or outdated?",
        type: "boolean",
        required: true,
      },
      {
        key: "gapDetails",
        prompt: "Briefly describe which policy(ies) and what's outstanding.",
        type: "text",
        showIf: { key: "hasKnownGap", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_091_REQUIRED_POSTINGS_NOTICES: {
    label: "Required Postings / Notices",
    reference: "WAC 388-76-10525; WAC 388-76-10890",
    description:
      "Confirm the licensing office contact, department hotline/ombuds poster, and advocacy poster are posted where anyone can see them, and that an evacuation floor plan is displayed on each floor.",
    icon: "ti-pin",
    questions: [
      {
        key: "licensingOfficeContactPosted",
        prompt:
          "Is the name, address, and telephone number of the home's regional Residential Care Services licensing office posted?",
        type: "boolean",
        required: true,
      },
      {
        key: "departmentHotlinePosterPosted",
        prompt:
          "Is the department's poster with the Complaint Resolution Unit hotline and the state ombuds program telephone number posted?",
        type: "boolean",
        required: true,
      },
      {
        key: "advocacyPosterPosted",
        prompt:
          "Is the poster from the protection and advocacy agency for residents with disabilities posted?",
        type: "boolean",
        required: true,
      },
      {
        key: "postedInCommonUseArea",
        prompt:
          "Are all of those postings displayed in a common use area where residents, resident representatives, department staff, and visitors can easily see them?",
        type: "boolean",
        required: true,
      },
      {
        key: "floorPlanOnEachFloor",
        prompt:
          "Is an emergency evacuation floor plan displayed on each floor of the home, in a visible location commonly used by residents, staff, and visitors?",
        type: "boolean",
        required: true,
      },
      {
        key: "floorPlanShowsRoute",
        prompt:
          "Does each posted floor plan show the evacuation route from that floor's rooms to the designated safe location outside the home?",
        type: "boolean",
        required: true,
      },
      {
        key: "postingsCurrentVersion",
        prompt:
          "Are the posted notices and contact information the current versions (for example, the latest department posters and correct licensing office details)?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_092_INSPECTION_RESULTS_AVAILABILITY: {
    label: "Inspection Results Availability",
    reference: "WAC 388-76-10585",
    description:
      "Confirm the most recent inspection and complaint investigation reports are viewable in a common area without asking, and that a notice is posted for the past three years' reports.",
    icon: "ti-clipboard-list",
    questions: [
      {
        key: "hasInspectionReport",
        prompt: "Has the home received any DSHS inspection report?",
        type: "boolean",
        required: true,
      },
      {
        key: "mostRecentInspectionInCommonArea",
        prompt:
          "Are the most recent inspection report, any related follow-up reports, and related cover letters placed in a common use area where anyone can view them without having to ask?",
        type: "boolean",
        showIf: { key: "hasInspectionReport", equals: true },
        required: true,
      },
      {
        key: "hasComplaintInvestigationReports",
        prompt:
          "Has the home received any complaint investigation reports since the most recent inspection or within the last twelve months, whichever is longer?",
        type: "boolean",
        required: true,
      },
      {
        key: "complaintReportsInCommonArea",
        prompt:
          "Are all of those reports, any follow-up reports, and related cover letters placed in a common use area where anyone can view them without having to ask?",
        type: "boolean",
        showIf: { key: "hasComplaintInvestigationReports", equals: true },
        required: true,
      },
      {
        key: "availabilityNoticePosted",
        prompt:
          "Is a notice posted stating that inspection reports and complaint investigation reports (with cover letters) from the past three years are available for review on request?",
        type: "boolean",
        required: true,
      },
      {
        key: "threeYearReportsAvailable",
        prompt:
          "Are copies of every inspection report, complaint investigation report, and related cover letter the home has received in the past three years on file and available to show on request?",
        type: "boolean",
        required: true,
      },
      {
        key: "keptCurrent",
        prompt:
          "Are the posted reports replaced or updated whenever a new inspection or complaint investigation report is received?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_015_BACKGROUND_INFORMATION_CONFIDENTIALITY: {
    label: "Background Information Confidentiality",
    reference: "WAC 388-76-10170; WAC 388-76-10325",
    description:
      "Confirm background check results and confidential personnel information are securely stored, access-restricted, and never improperly disclosed.",
    icon: "ti-lock",
    questions: [
      {
        key: "storedSeparatelyAndSecured",
        prompt:
          "Are background check results, criminal history details, and confidential medical information (for example, TB test results) stored separately from general personnel files, in a locked or encrypted location?",
        type: "boolean",
        required: true,
      },

      {
        key: "accessRestrictedToAuthorized",
        prompt:
          "Is access to this confidential information restricted to the provider, entity representative, or a designated HR administrator?",
        type: "boolean",
        required: true,
      },

      {
        key: "noUnauthorizedDisclosure",
        prompt:
          "Can you confirm no background check result has been shared, published, or disclosed to unauthorized staff or third parties (other than authorized DSHS/RCS inspectors)?",
        type: "boolean",
        required: true,
      },

      {
        key: "selfReportingPolicyInPlace",
        prompt:
          "Are staff and household members required to notify the provider within 24 hours of any new arrest, charge, conviction, or pending action?",
        type: "boolean",
        required: true,
      },

      {
        key: "gapDetails",
        prompt: "What's stored improperly, or who has unauthorized access?",
        type: "text",
        showIf: { key: "storedSeparatelyAndSecured", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_017_PERSONNEL_RECORD_RETENTION: {
    label: "Personnel Record Retention",
    reference: "WAC 388-76-10198",
    description:
      "Confirm former staff records are retained for the required period after employment ends and disposed of securely.",
    icon: "ti-archive",
    questions: [
      {
        key: "hasFormerStaff",
        prompt:
          "Has the home had any staff, volunteers, or household members whose employment or service has ended?",
        type: "boolean",
        required: true,
      },
      {
        key: "retainedThreeYears",
        prompt:
          "Are required records for former staff, volunteers, or household members retained for at least 3 years after separation?",
        type: "boolean",
        showIf: { key: "hasFormerStaff", equals: true },
        required: true,
      },
      {
        key: "separationDateLogged",
        prompt:
          "Is the separation date recorded and the file moved to inactive/retention storage promptly after separation?",
        type: "boolean",
        showIf: { key: "hasFormerStaff", equals: true },
        required: true,
      },
      {
        key: "securelyDisposed",
        prompt:
          "Are records that have passed their retention window destroyed securely (e.g. cross-cut shredding, permanent digital purge) rather than simply discarded?",
        type: "boolean",
        showIf: { key: "hasFormerStaff", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_028_EVACUATION_MISSING_RESIDENT_CONTINUITY_EVENT_REPORTING: {
    label: "Evacuation / Missing Resident / Continuity Event Reporting",
    reference: "WAC 388-76-10225(1)(b)",
    description:
      "Confirm evacuation events, threats to continued care, and missing residents were reported to the department by hotline or online report.",
    icon: "ti-urgent",
    questions: [
      {
        key: "hadEvacuationEvent",
        prompt:
          "Since the last check, has there been an actual or potential event that required any resident to be evacuated?",
        type: "boolean",
        required: true,
      },
      {
        key: "evacuationReported",
        prompt:
          "Was each of those events reported to the department (complaint hotline or online report)?",
        type: "boolean",
        showIf: { key: "hadEvacuationEvent", equals: true },
        required: true,
      },
      {
        key: "hadCareContinuityThreat",
        prompt:
          "Since the last check, has any condition threatened your ability to continue providing care or services to each resident?",
        type: "boolean",
        required: true,
      },
      {
        key: "continuityThreatReported",
        prompt:
          "Was each of those conditions reported to the department (complaint hotline or online report)?",
        type: "boolean",
        showIf: { key: "hadCareContinuityThreat", equals: true },
        required: true,
      },
      {
        key: "hadMissingResident",
        prompt: "Since the last check, has any resident been missing?",
        type: "boolean",
        required: true,
      },
      {
        key: "missingResidentReported",
        prompt:
          "Was each missing resident reported to the department (complaint hotline or online report)?",
        type: "boolean",
        showIf: { key: "hadMissingResident", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_031_STATE_FUNDED_RESIDENT_MEDICAL_LEAVE_NOTICE: {
    label: "State-Funded Resident Medical Leave Notice",
    reference: "WAC 388-76-10220; WAC 388-105-0045",
    description:
      "Confirm DSHS case manager notice, bed-hold tracking, billing limits, and re-admission review for a state-funded resident's medical leave.",
    icon: "ti-ambulance",
    questions: [
      {
        key: "hadMedicalLeave",
        prompt:
          "Since the last check, has a state-funded (Medicaid) resident left for hospitalization or acute/skilled nursing care?",
        type: "boolean",
        required: true,
      },

      {
        key: "transferDetailsDocumented",
        prompt:
          "Is the transfer documented with transport time, receiving facility, and admitting diagnosis or reason?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "caseManagerNotifiedWithin24Hours",
        prompt:
          "Was the resident's DSHS/HCS or AAA case manager notified within 24 hours of the transfer?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "noticeMethodLogged",
        prompt:
          "Is the date, time, and method (email, fax, or phone) of that notice logged?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "writtenNoticeFormCompleted",
        prompt:
          "Was the formal written notice completed, including hospitalization date/time, facility name, physician contact, and reason for transfer?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "bedHoldTracked",
        prompt:
          "Is the bed-hold period being tracked against the state-authorized limit (up to 20 days per event, when approved by HCS)?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "noUnauthorizedBilling",
        prompt:
          "Has the home avoided billing the resident or family for bed-hold fees beyond what's authorized (no separate private agreement in place)?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "medicationsSecuredAndMarSuspended",
        prompt:
          "Were the resident's medications secured and accounted for, and was daily MAR logging suspended while the resident was away?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "hasReturned",
        prompt:
          "Has the resident since returned or been re-admitted to the home?",
        type: "boolean",
        showIf: { key: "hadMedicalLeave", equals: true },
        required: true,
      },

      {
        key: "reAdmissionReviewCompleted",
        prompt:
          "Before re-admission, did the home review the updated hospital discharge summary and new medical orders?",
        type: "boolean",
        showIf: { key: "hasReturned", equals: true },
        required: true,
      },

      {
        key: "careNeedsStillMet",
        prompt:
          "Can the home still safely meet the resident's needs, including any new or increased care needs from the hospitalization?",
        type: "boolean",
        showIf: { key: "hasReturned", equals: true },
        required: true,
      },

      {
        key: "gapDetails",
        prompt: "Describe what's missing or unresolved.",
        type: "text",
        showIf: { key: "careNeedsStillMet", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_055_CONTROLLED_MEDICATION_ACCOUNTABILITY: {
    label: "Controlled Medication Accountability",
    reference: "Applicable medication/accountability rules; facility policy",
    description:
      "Confirm controlled medication counts are reconciled and any discrepancies are explained.",
    icon: "ti-pill",
    questions: [
      {
        key: "hasControlledMedications",
        prompt:
          "Does the home currently hold any controlled substance medications for residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "countsReconciled",
        prompt:
          "Are controlled medication counts reconciled against the records?",
        type: "boolean",
        showIf: { key: "hasControlledMedications", equals: true },
        required: true,
      },
      {
        key: "lastCountDate",
        prompt: "When was the last count or reconciliation done?",
        type: "date",
        showIf: { key: "hasControlledMedications", equals: true },
        required: true,
      },
      {
        key: "noUnexplainedDiscrepancies",
        prompt: "Are there zero unexplained discrepancies?",
        type: "boolean",
        showIf: { key: "hasControlledMedications", equals: true },
        required: true,
      },
      {
        key: "dispositionDocumented",
        prompt:
          "Is the disposition of any returned, disposed, or transferred controlled medication documented?",
        type: "boolean",
        showIf: { key: "hasControlledMedications", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_057_MEDICAL_DEVICES: {
    label: "Medical Devices",
    reference: "WAC 388-76-10430; WAC 388-76-10440; WAC 388-76-10600",
    description:
      "Confirm resident medical devices have a current order, delegated/trained staff, maintenance logs, and a backup power plan for life-sustaining equipment.",
    icon: "ti-wheelchair",
    questions: [
      {
        key: "hasDevices",
        prompt:
          "Do any residents use medical devices or adaptive equipment (for example, oxygen, CPAP, feeding pumps, catheters, lifts, or bed rails)?",
        type: "boolean",
        required: true,
      },

      {
        key: "currentPractitionerOrder",
        prompt:
          "Is there a current, signed practitioner order in each resident's record specifying the exact operating parameters for their device(s)?",
        type: "boolean",
        showIf: { key: "hasDevices", equals: true },
        required: true,
      },

      {
        key: "hasDelegationRequiredDevices",
        prompt:
          "Do any of these devices require Nurse Delegation for non-licensed staff (for example, feeding pumps, suctioning, or medicated nebulizer treatments)?",
        type: "boolean",
        showIf: { key: "hasDevices", equals: true },
        required: true,
      },

      {
        key: "delegationOrTrainingDocumented",
        prompt:
          "Is Nurse Delegation or documented staff competency training on file for every caregiver operating those devices?",
        type: "boolean",
        showIf: { key: "hasDelegationRequiredDevices", equals: true },
        required: true,
      },

      {
        key: "maintenanceLogCurrent",
        prompt:
          "Is a device maintenance and sanitation log current, tracking cleanings, filter changes, and equipment checks?",
        type: "boolean",
        showIf: { key: "hasDevices", equals: true },
        required: true,
      },

      {
        key: "hasBedRails",
        prompt: "Does any resident use bed rails or side assist bars?",
        type: "boolean",
        showIf: { key: "hasDevices", equals: true },
        required: true,
      },

      {
        key: "bedRailsAssessedNotRestraint",
        prompt:
          "Has each been assessed for entanglement/trapping hazards, with resident consent confirming it isn't a physical restraint?",
        type: "boolean",
        showIf: { key: "hasBedRails", equals: true },
        required: true,
      },

      {
        key: "hasLifeSustainingDevices",
        prompt:
          "Does any resident depend on life-sustaining equipment (for example, oxygen or a feeding pump)?",
        type: "boolean",
        showIf: { key: "hasDevices", equals: true },
        required: true,
      },

      {
        key: "backupPowerPlanDocumented",
        prompt:
          "Is an emergency battery or backup power plan for that equipment documented in the disaster preparedness plan?",
        type: "boolean",
        showIf: { key: "hasLifeSustainingDevices", equals: true },
        required: true,
      },

      {
        key: "gapDetails",
        prompt: "Which residents or devices are affected?",
        type: "text",
        showIf: { key: "currentPractitionerOrder", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_062_COMPLAINTS_GRIEVANCES: {
    label: "Complaints / Grievances",
    reference: "WAC 388-76-10510; RCW 70.129.030; WAC 388-76-10590",
    description:
      "Confirm complaints are logged, acknowledged and resolved on time, external contacts are posted, and no one faced retaliation.",
    icon: "ti-message-report",
    questions: [
      {
        key: "hadComplaint",
        prompt:
          "Since the last check, has any resident, family member, or representative raised a complaint or grievance?",
        type: "boolean",
        required: true,
      },

      {
        key: "loggedOnIntake",
        prompt:
          "Was each one recorded in the grievance log (date received, complainant, concern, assigned investigator)?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
        required: true,
      },

      {
        key: "acknowledgedWithin24Hours",
        prompt:
          "Was each complaint acknowledged, verbally or in writing, within 24 hours?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
        required: true,
      },

      {
        key: "involvedMisconductOrNeglect",
        prompt:
          "Did any of these complaints involve caregiver misconduct or care neglect?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
      },

      {
        key: "immediateSafetyMeasuresTaken",
        prompt:
          "Were immediate safety measures put in place while that complaint was investigated?",
        type: "boolean",
        showIf: { key: "involvedMisconductOrNeglect", equals: true },
        required: true,
      },

      {
        key: "investigationConducted",
        prompt:
          "Was each complaint investigated, including relevant staff interviews and a review of progress logs or MARs where applicable?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
        required: true,
      },

      {
        key: "writtenResponseWithin14Days",
        prompt:
          "Was a written summary of findings and corrective actions delivered to the complainant within 14 calendar days (sooner if there was a safety or care risk)?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
        required: true,
      },

      {
        key: "resolutionLetterOnFile",
        prompt:
          "Is a copy of the final resolution letter, signed by the provider and the resident or representative, on file?",
        type: "boolean",
        showIf: { key: "hadComplaint", equals: true },
        required: true,
      },

      {
        key: "noRetaliation",
        prompt:
          "Can you confirm no resident faced coercion, discrimination, loss of privileges, a discharge threat, or care disruption for filing a complaint or contacting an advocate?",
        type: "boolean",
        required: true,
      },

      {
        key: "ombudsmanAndHotlinePosted",
        prompt:
          "Are the DSHS Complaint Resolution Unit hotline and the LTC Ombudsman contact information posted in a common area accessible to residents and visitors?",
        type: "boolean",
        required: true,
      },

      {
        key: "gapDetails",
        prompt: "Which complaints or requirements are unresolved?",
        type: "text",
        showIf: { key: "writtenResponseWithin14Days", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_081_FOOD_AND_NUTRITION: {
    label: "Food and Nutrition",
    reference: "WAC 388-76-10415; WAC 388-76-10420",
    description:
      "Confirm staff food handling training, meal frequency and snacks, resident-specific dietary accommodation, physician approval for modified diets, and safe, sufficient food.",
    icon: "ti-tools-kitchen-2",
    questions: [
      {
        key: "foodHandlingTrainingMet",
        prompt:
          "Have all staff who prepare or serve food met the safe food handling training requirements (chapter 388-112A WAC)?",
        type: "boolean",
        required: true,
      },
      {
        key: "mealsServedInHome",
        prompt: "Are meals served in the home where each resident lives?",
        type: "boolean",
        required: true,
      },
      {
        key: "threeMealsAtRegularTimes",
        prompt:
          "Are at least three meals served in every 24-hour period, at regular times comparable to normal meal times in the community?",
        type: "boolean",
        required: true,
      },
      {
        key: "snacksAvailable",
        prompt:
          "Are nutritious snacks available to residents between meals and in the evening?",
        type: "boolean",
        required: true,
      },
      {
        key: "residentInputInMealPlanning",
        prompt: "Do residents have input into meal planning and scheduling?",
        type: "boolean",
        required: true,
      },
      {
        key: "mealsAccommodatePreferencesAndCulture",
        prompt:
          "Do meals accommodate each resident's food preferences and cultural or ethnic background?",
        type: "boolean",
        required: true,
      },
      {
        key: "nutritionalNeedsMet",
        prompt: "Do meals meet each resident's caloric and nutritional needs?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasSpecialDiets",
        prompt:
          "Do any residents have prescribed or special dietary requirements, including food allergies or sensitivities, or a condition that makes chewing or swallowing difficult?",
        type: "boolean",
        required: true,
      },
      {
        key: "dietaryNeedsReflected",
        prompt: "Are all of those needs reflected in the meals served?",
        type: "boolean",
        showIf: { key: "hasSpecialDiets", equals: true },
        required: true,
      },
      {
        key: "hasModifiedDietsOrSupplements",
        prompt:
          "Does any resident receive nutrient concentrates, supplements, or a modified diet (for example, pureed, mechanical soft, or thickened liquids)?",
        type: "boolean",
        required: true,
      },
      {
        key: "physicianApprovalOnFile",
        prompt:
          "Is written approval from the resident's physician on file for each of those?",
        type: "boolean",
        showIf: { key: "hasModifiedDietsOrSupplements", equals: true },
        required: true,
      },
      {
        key: "onlyPasteurizedMilk",
        prompt: "Is only pasteurized milk served in the home?",
        type: "boolean",
        required: true,
      },
      {
        key: "servesHomeCannedFoods",
        prompt: "Does the home serve any home-canned foods?",
        type: "boolean",
        required: true,
      },
      {
        key: "homeCanningFollowsGuidelines",
        prompt:
          "Is all home-canned food processed according to the latest county cooperative extension service guidelines?",
        type: "boolean",
        showIf: { key: "servesHomeCannedFoods", equals: true },
        required: true,
      },
      {
        key: "foodSufficient",
        prompt: "Is food in sufficient supply for all residents?",
        type: "boolean",
        required: true,
      },
      {
        key: "foodSafe",
        prompt:
          "Is food stored, prepared, and served safely, with no unsafe, unsanitary, or contaminated food condition present?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_090_STAFF_POLICY_ACKNOWLEDGEMENT_IMPLEMENTATION: {
    label: "Staff Policy Acknowledgement / Implementation",
    reference: "WAC 388-76-10130; WAC 388-76-10140",
    description:
      "Confirm staff signed acknowledgement of required policies before working unsupervised, understood them, and re-signed after any policy update.",
    icon: "ti-signature",
    questions: [
      {
        key: "allSignedBeforeUnshadowedShift",
        prompt:
          "Has every active staff member signed the policy acknowledgement form before working an un-shadowed shift?",
        type: "boolean",
        required: true,
      },

      {
        key: "coversRequiredPolicies",
        prompt:
          "Does the acknowledgement cover all required policies (mandated reporting/abuse reporting, resident rights & dignity, infection control, medication management, emergency/evacuation duties, and the photo/social media confidentiality rule)?",
        type: "boolean",
        required: true,
      },

      {
        key: "signedFormsInPersonnelFiles",
        prompt:
          "Is a signed, dated acknowledgement page on file in each active staff member's personnel record, matching the current policy manual version?",
        type: "boolean",
        required: true,
      },

      {
        key: "explainedInUnderstoodLanguage",
        prompt:
          "Were the policies explained in a language each staff member understands, not just presented for signature?",
        type: "boolean",
        required: true,
      },

      {
        key: "hadPolicyUpdate",
        prompt:
          "Since the last check, has the policy manual or house rules been updated?",
        type: "boolean",
        required: true,
      },

      {
        key: "reSignedWithin14Days",
        prompt:
          "Did all affected staff receive the written update and provide a re-signed acknowledgement within 14 days?",
        type: "boolean",
        showIf: { key: "hadPolicyUpdate", equals: true },
        required: true,
      },

      {
        key: "annualReSignCurrent",
        prompt:
          "Is each staff member's annual re-acknowledgement current (within the last 12 months)?",
        type: "boolean",
        required: true,
      },

      {
        key: "gapDetails",
        prompt:
          "Which staff members, or which policies, are missing an acknowledgement?",
        type: "text",
        showIf: { key: "allSignedBeforeUnshadowedShift", equals: false },
        required: true,
      },
    ],
  },

  WA_AFH_026_INCIDENT_LOG: {
    label: "Incident Log",
    reference: "WAC 388-76-10200; WAC 388-76-10205; WAC 388-76-10220",
    description:
      "Confirm every incident since your last check was entered in the system, the log is accessible to staff, and it is reviewed for patterns.",
    icon: "ti-notebook",
    questions: [
      {
        key: "hadIncidents",
        prompt:
          "Since the last check, did any incident, accident, fall, injury, medication error, behavioral event, medical emergency, or other unusual event involving a resident occur in or around the home?",
        type: "boolean",
        required: true,
      },
      {
        key: "incidentCount",
        prompt: "About how many occurred?",
        type: "number",
        showIf: { key: "hadIncidents", equals: true },
        required: true,
      },
      {
        key: "allLogged",
        prompt: "Has every one of them been entered in the incident system?",
        type: "boolean",
        showIf: { key: "hadIncidents", equals: true },
        required: true,
      },
      {
        key: "unloggedCount",
        prompt: "How many have not been entered yet?",
        type: "number",
        showIf: { key: "allLogged", equals: false },
        required: true,
      },
      {
        key: "unloggedReason",
        prompt:
          "Why weren't they entered (for example, system outage, discovered late, unsure it was reportable)? Please enter them now if you can.",
        type: "text",
        showIf: { key: "allLogged", equals: false },
        required: true,
      },
      {
        key: "crossReferencedWithCarePlans",
        prompt:
          "Do the entries match the residents' progress notes, and were care plans updated after any significant incident or fall?",
        type: "boolean",
        showIf: { key: "hadIncidents", equals: true },
        required: true,
      },
      {
        key: "logAccessible",
        prompt:
          "Is the incident log immediately accessible to all staff on duty and ready to show DSHS surveyors?",
        type: "boolean",
        required: true,
      },
      {
        key: "lastQaReviewDate",
        prompt:
          "When was the incident log last reviewed for patterns or recurring hazards (for example, frequent fall times or repeat medication errors)?",
        type: "date",
        required: true,
      },
    ],
  },

  WA_AFH_027_ABUSE_NEGLECT_EXPLOITATION_ABANDONMENT_REPORTING: {
    label: "Abuse, Neglect, Exploitation & Abandonment Reporting",
    reference:
      "RCW 74.34; WAC 388-76-10220; WAC 388-76-10225; WAC 388-76-10675",
    description:
      "Confirm suspected abuse, neglect, exploitation, or abandonment since your last check was reported and entered, and that reporter training and acknowledgments are in place.",
    icon: "ti-shield-exclamation",
    questions: [
      {
        key: "hadSuspectedAneg",
        prompt:
          "Since the last check, was any abuse, neglect, financial exploitation (including theft or missing money or property), or abandonment of a resident suspected, alleged, or witnessed?",
        type: "boolean",
        required: true,
      },
      {
        key: "anegCount",
        prompt: "How many separate events?",
        type: "number",
        showIf: { key: "hadSuspectedAneg", equals: true },
        required: true,
      },
      {
        key: "allLogged",
        prompt: "Has each one been entered as an incident in the system?",
        type: "boolean",
        showIf: { key: "hadSuspectedAneg", equals: true },
        required: true,
      },
      {
        key: "unloggedCount",
        prompt: "How many have not been entered yet?",
        type: "number",
        showIf: { key: "allLogged", equals: false },
        required: true,
      },
      {
        key: "unloggedReason",
        prompt: "Why weren't they entered? Please enter them now if you can.",
        type: "text",
        showIf: { key: "allLogged", equals: false },
        required: true,
      },
      {
        key: "allReportedToDshs",
        prompt:
          "Was each one reported immediately to the DSHS CRU hotline (1-800-562-6078) or online intake?",
        type: "boolean",
        showIf: { key: "hadSuspectedAneg", equals: true },
        required: true,
      },
      {
        key: "involvedSuspectedCrime",
        prompt:
          "Did any involve suspected criminal conduct (for example assault, sexual misconduct, or theft)?",
        type: "boolean",
        showIf: { key: "hadSuspectedAneg", equals: true },
        required: true,
      },
      {
        key: "allReportedToPolice",
        prompt: "Was each of those reported immediately to law enforcement?",
        type: "boolean",
        showIf: { key: "involvedSuspectedCrime", equals: true },
        required: true,
      },
      {
        key: "reporterTrainingCurrent",
        prompt:
          "Has every staff member completed mandatory reporter training before working with residents, and again each year?",
        type: "boolean",
        required: true,
      },
      {
        key: "reporterAcknowledgmentsSigned",
        prompt:
          "Is a signed mandatory reporter acknowledgment in every employee's personnel file?",
        type: "boolean",
        required: true,
      },
      {
        key: "antiRetaliationPosted",
        prompt:
          "Is the anti-retaliation policy for staff and residents who report in good faith posted?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_029_SIGNIFICANT_CONDITION_CHANGE_SERIOUS_INJURY_TRAUMA_DEATH_NOTIFICATIONS:
    {
      label: "Condition Change, Serious Injury, Trauma & Death Notifications",
      reference: "WAC 388-76-10220; WAC 388-76-10225; RCW 70.128.130",
      description:
        "Confirm significant changes in condition, serious injuries, trauma falls, hospital transfers, and deaths since your last check were entered and that the required parties were notified.",
      icon: "ti-heartbeat",
      questions: [
        {
          key: "hadCriticalEvent",
          prompt:
            "Since the last check, did any resident have a significant change in condition, a serious injury (ER, urgent care, stitches, or fracture), a trauma or unwitnessed fall, a hospital transfer, or die?",
          type: "boolean",
          required: true,
        },
        {
          key: "criticalEventCount",
          prompt: "How many separate events?",
          type: "number",
          showIf: { key: "hadCriticalEvent", equals: true },
          required: true,
        },
        {
          key: "allLogged",
          prompt: "Has each one been entered as an incident in the system?",
          type: "boolean",
          showIf: { key: "hadCriticalEvent", equals: true },
          required: true,
        },
        {
          key: "unloggedCount",
          prompt: "How many have not been entered yet?",
          type: "number",
          showIf: { key: "allLogged", equals: false },
          required: true,
        },
        {
          key: "unloggedReason",
          prompt: "Why weren't they entered? Please enter them now if you can.",
          type: "text",
          showIf: { key: "allLogged", equals: false },
          required: true,
        },
        {
          key: "involvedMedicaidResident",
          prompt:
            "Did any of these involve a Medicaid (state-funded) resident?",
          type: "boolean",
          showIf: { key: "hadCriticalEvent", equals: true },
          required: true,
        },
        {
          key: "caseManagerNotifiedWithin24h",
          prompt:
            "Was the DSHS case manager notified within 24 hours (or the next business day) for each of those?",
          type: "boolean",
          showIf: { key: "involvedMedicaidResident", equals: true },
          required: true,
        },
        {
          key: "involvedHospiceResident",
          prompt: "Did any of these involve a resident enrolled in hospice?",
          type: "boolean",
          showIf: { key: "hadCriticalEvent", equals: true },
          required: true,
        },
        {
          key: "hospiceNotified",
          prompt:
            "Was the hospice team notified immediately for each of those?",
          type: "boolean",
          showIf: { key: "involvedHospiceResident", equals: true },
          required: true,
        },
        {
          key: "progressNotesComplete",
          prompt:
            "Do the residents' progress notes for each event record vitals, observations, exact notification times, and any physician or emergency orders?",
          type: "boolean",
          showIf: { key: "hadCriticalEvent", equals: true },
          required: true,
        },
        {
          key: "emergencyContactListPosted",
          prompt:
            "Is an up-to-date emergency contact list (family, physicians, case managers) posted in the staff area?",
          type: "boolean",
          required: true,
        },
      ],
    },

  WA_AFH_030_FOOD_POISONING_COMMUNICABLE_DISEASE_OUTBREAK_REPORTING: {
    label: "Food Poisoning / Communicable Disease Outbreak Reporting",
    reference: "WAC 388-76; RCW 70.05",
    description:
      "Confirm any outbreak or reportable disease since your last check was entered, reported within 24 hours, controlled, and properly closed.",
    icon: "ti-virus",
    questions: [
      {
        key: "hadOutbreakOrReportableDisease",
        prompt:
          "Since the last check, did two or more residents or staff have similar symptoms (vomiting, diarrhea, fever, rash, or respiratory illness) within 72 hours, or was anyone diagnosed with Hepatitis A, measles, TB, salmonellosis, shigellosis, or E. coli O157?",
        type: "boolean",
        required: true,
      },
      {
        key: "allLogged",
        prompt: "Has each one been entered as an incident in the system?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "unloggedReason",
        prompt: "Why weren't they entered? Please enter them now if you can.",
        type: "text",
        showIf: { key: "allLogged", equals: false },
        required: true,
      },
      {
        key: "reportedToLhjWithin24h",
        prompt:
          "Was the local health jurisdiction notified within 24 hours of identifying the cases?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "reportedToRcsWithin24h",
        prompt:
          "Was DSHS / Residential Care Services notified within 24 hours?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "isolationAndCleaningDone",
        prompt:
          "Were affected residents isolated, symptomatic staff excluded from care and food prep, and the environment disinfected?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "suspectedFoodborne",
        prompt: "Was a foodborne illness suspected?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "foodSamplesRetained",
        prompt:
          "Were samples of the suspected food (refrigerated, not frozen), packaging, and lot numbers kept for Public Health?",
        type: "boolean",
        showIf: { key: "suspectedFoodborne", equals: true },
        required: true,
      },
      {
        key: "outbreakClosed",
        prompt: "Has the outbreak been officially closed?",
        type: "boolean",
        showIf: { key: "hadOutbreakOrReportableDisease", equals: true },
        required: true,
      },
      {
        key: "lhjClearanceReceived",
        prompt: "Did the local health jurisdiction clear the outbreak?",
        type: "boolean",
        showIf: { key: "outbreakClosed", equals: true },
        required: true,
      },
      {
        key: "debriefFiled",
        prompt:
          "Is a final incident debrief filed in the home's administrative safety records?",
        type: "boolean",
        showIf: { key: "outbreakClosed", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_063_TRANSFER_DISCHARGE_PROCESS: {
    label: "Transfer and Discharge Process",
    reference: "WAC 388-76-10615; WAC 388-76-10616; WAC 388-76-10617",
    description:
      "Confirm that any resident transfer or discharge since the last check had a permitted reason, followed the notice requirements (including Medicaid-payor requirements), and was handled with accommodation and preparation.",
    icon: "ti-transfer",
    questions: [
      {
        key: "hadTransferOrDischarge",
        prompt:
          "Since the last check, were any residents transferred or discharged from the adult family home?",
        type: "boolean",
        required: true,
      },
      {
        key: "allDischargesHadPermittedReason",
        prompt:
          "For every transfer or discharge, was the reason one permitted under WAC 388-76-10615 (the resident's needs cannot be met, others' safety or health is endangered, non-payment, or the home ceasing to operate)?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "reasonableAccommodationsAttempted",
        prompt:
          "Before each transfer or discharge, did the home first attempt reasonable accommodations to avoid it, unless the resident agreed to the move?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "residentsPreparedForTransfer",
        prompt:
          "Were affected residents given enough preparation and orientation to support a safe and orderly transfer or discharge?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "writtenNoticeProvided",
        prompt:
          "Was written notice provided to each affected resident and the resident's representative?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "shortNoticeUsed",
        prompt:
          "Was shortened notice (less than 30 days) used for any transfer or discharge?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "shortNoticeHadValidReason",
        prompt:
          "For every shortened-notice case, did a permitted exception apply (others' safety or health endangered, urgent medical needs requiring an immediate move, or the resident absent from the home for 30 or more days)?",
        type: "boolean",
        showIf: { key: "shortNoticeUsed", equals: true },
        required: true,
      },
      {
        key: "shortNoticeReasonDocumented",
        prompt: "Was the reason for using shortened notice documented?",
        type: "boolean",
        showIf: { key: "shortNoticeUsed", equals: true },
        required: true,
      },
      {
        key: "thirtyDayNoticeProvided",
        prompt:
          "For every transfer or discharge where no shortened-notice exception applied, was written notice given at least 30 days before the move? (Answer yes if every case used a permitted shortened notice.)",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "noticeIncludedReason",
        prompt:
          "Did every written notice state the reason for the transfer or discharge?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "noticeIncludedEffectiveDate",
        prompt: "Did every written notice state the effective date?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "noticeIncludedDestinationWhenKnown",
        prompt:
          "Did every written notice state the location the resident was moving to, whenever that was known at the time of the notice?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "noticeIncludedOmbudsInformation",
        prompt:
          "Did every written notice include the name, address, and telephone number of the state long-term care ombuds?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "hadResidentWithDdOrMentalIllness",
        prompt:
          "Did any of those residents have a developmental disability or a mental illness?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "requiredProtectionAdvocacyInfoIncluded",
        prompt:
          "Did their notices include the mailing address and telephone number of the agency responsible for protection and advocacy for individuals with that condition?",
        type: "boolean",
        showIf: { key: "hadResidentWithDdOrMentalIllness", equals: true },
        required: true,
      },
      {
        key: "hadKnownInterestedFamilyMember",
        prompt:
          "Was there a known interested family member for any of those residents?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "interestedFamilyNotificationAttempted",
        prompt:
          "Did the home make a reasonable effort to notify each known interested family member?",
        type: "boolean",
        showIf: { key: "hadKnownInterestedFamilyMember", equals: true },
        required: true,
      },
      {
        key: "noticeUnderstandableToResident",
        prompt:
          "Was each notice provided in a language and manner the resident understands?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "noticeCopyRetained",
        prompt:
          "Was a copy of each written notice kept in the resident's record?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "hadMedicaidPayorResident",
        prompt: "Did any of those residents have Medicaid as a payor?",
        type: "boolean",
        showIf: { key: "hadTransferOrDischarge", equals: true },
        required: true,
      },
      {
        key: "medicaidNoticeIncludedLegalCounsel",
        prompt:
          "Did each of those notices include the department's contact information for the legal services agencies and the statement that, subject to legislative appropriation, free legal counsel is available?",
        type: "boolean",
        showIf: { key: "hadMedicaidPayorResident", equals: true },
        required: true,
      },
      {
        key: "medicaidPrescribedFormUsed",
        prompt:
          "Was each of those notices issued in substantially the prescribed notice form for residents with Medicaid as a payor?",
        type: "boolean",
        showIf: { key: "hadMedicaidPayorResident", equals: true },
        required: true,
      },
      {
        key: "medicaidCopyToCaseManager",
        prompt:
          "Within three calendar days of issuing each of those notices, was a copy provided to the resident's assigned department case manager?",
        type: "boolean",
        showIf: { key: "hadMedicaidPayorResident", equals: true },
        required: true,
      },
      {
        key: "medicaidCopyToOmbudsProgram",
        prompt:
          "Within three calendar days of issuing each of those notices, was a copy provided to the Washington state long-term care ombudsman program?",
        type: "boolean",
        showIf: { key: "hadMedicaidPayorResident", equals: true },
        required: true,
      },
    ],
  },

  WA_AFH_019_CPR_FIRST_AID_DOCUMENTATION: {
    label: "CPR / First Aid Documentation",
    reference: "WAC 388-76-10146; WAC 388-76-10198; WAC 388-112A-0720",
    description:
      "Confirm required staff have completed CPR and first aid training, the provider and managers hold valid cards, and training records are on file and accessible to department staff.",
    icon: "ti-first-aid-kit",
    questions: [
      {
        key: "allRequiredStaffTrained",
        prompt:
          "Have all caregivers, entity representatives, and resident managers completed the CPR and first aid training required under chapter 388-112A WAC (for staff hired before January 7, 2012, the training required when they were hired)?",
        type: "boolean",
        required: true,
      },
      {
        key: "cprRecordOnFile",
        prompt:
          "Does every applicable staff member's personnel record include their cardiopulmonary resuscitation (CPR) training record?",
        type: "boolean",
        required: true,
      },
      {
        key: "firstAidRecordOnFile",
        prompt:
          "Does every applicable staff member's personnel record include their first aid training record?",
        type: "boolean",
        required: true,
      },
      {
        key: "leadershipCardsValid",
        prompt:
          "Do the provider, the entity representative (if any), and the resident manager each hold a valid, unexpired CPR and first-aid card or certificate?",
        type: "boolean",
        required: true,
      },
      {
        key: "currentCprDocumentation",
        prompt:
          "Is every CPR and first aid card or certificate on file for staff currently valid (none expired)?",
        type: "boolean",
        required: true,
      },
      {
        key: "recordsReadilyAccessible",
        prompt:
          "Are CPR and first aid training records kept in a place readily accessible to authorized department staff?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_016_PERSONNEL_RECORDS_COMPLETENESS: {
    label: "Personnel Records Completeness",
    reference: "WAC 388-76-10198",
    description:
      "Confirm required personnel records are complete and readily accessible for all applicable staff. Staff contact and address details are checked from staff profiles.",
    icon: "ti-folder-check",
    questions: [
      {
        key: "allRequiredTrainingRecordsOnFile",
        prompt:
          "For every applicable staff member, are all required orientation and training records (including chapter 388-112A WAC training, specialty training, nurse delegation core training, and continuing education) maintained in the personnel file?",
        type: "boolean",
        required: true,
      },
      {
        key: "cprFirstAidRecordsOnFile",
        prompt:
          "Are required CPR and first aid records maintained for every applicable staff member?",
        type: "boolean",
        required: true,
      },
      {
        key: "hivAidsTrainingRecordsOnFile",
        prompt:
          "Are required HIV/AIDS training records maintained for every applicable staff member?",
        type: "boolean",
        required: true,
      },
      {
        key: "tbResultsOnFile",
        prompt:
          "Are required tuberculosis testing results maintained for every applicable staff member?",
        type: "boolean",
        required: true,
      },
      {
        key: "backgroundRecordsOnFile",
        prompt:
          "Are required criminal history disclosures and background check results maintained for every applicable staff member?",
        type: "boolean",
        required: true,
      },
      {
        key: "recordsReadilyAccessible",
        prompt:
          "Are personnel records kept in a place readily accessible to authorized department staff?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_018_ORIENTATION_BASIC_SPECIALTY_TRAINING_RECORDS: {
    label: "Orientation, Basic & Specialty Training Records",
    reference: "WAC 388-76-10198; WAC 388-112A-0118; Chapter 388-112A WAC",
    description:
      "Confirm required staff training has been completed and that proof of completion with the required details is kept in personnel records.",
    icon: "ti-certificate",
    questions: [
      {
        key: "orientationCompleted",
        prompt:
          "Have all staff who require orientation completed the required orientation training?",
        type: "boolean",
        required: true,
      },
      {
        key: "basicTrainingCompleted",
        prompt:
          "Have all staff who require basic or modified basic training completed the applicable training?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasSpecialtyNeedResidents",
        prompt:
          "Does the home serve any resident whose primary special need is dementia, mental illness, or a developmental disability?",
        type: "boolean",
        required: true,
      },
      {
        key: "specialtyTrainingCompleted",
        prompt:
          "Have all staff who require specialty training completed the required specialty training?",
        type: "boolean",
        showIf: { key: "hasSpecialtyNeedResidents", equals: true },
        required: true,
      },
      {
        key: "staffPerformNurseDelegation",
        prompt: "Do any staff perform tasks delegated by a nurse?",
        type: "boolean",
        required: true,
      },
      {
        key: "nurseDelegationTrainingCompleted",
        prompt:
          "Have all staff who perform delegated tasks completed the required nurse delegation core training?",
        type: "boolean",
        showIf: { key: "staffPerformNurseDelegation", equals: true },
        required: true,
      },
      {
        key: "continuingEducationCurrent",
        prompt:
          "Is continuing education current for all staff for whom it is required?",
        type: "boolean",
        required: true,
      },
      {
        key: "trainingProofOnFile",
        prompt:
          "Is a copy of the proof of completion (certificate, transcript, or other proof) kept in the personnel record for every required training?",
        type: "boolean",
        required: true,
      },
      {
        key: "trainingProofContainsRequiredDetails",
        prompt:
          "Does each proof of completion show the student's name, the department-approved training title, the number of hours, the training entity's name and identification number, the instructor's name, an instructor or training entity signature, and the completion date (plus the department curriculum approval code for continuing education, and the instructor's identification number for core basic)?",
        type: "boolean",
        required: true,
      },
    ],
  },

  WA_AFH_032_RESIDENT_ADMISSION_APPROPRIATENESS: {
    label: "Resident Admission Appropriateness",

    reference:
      "WAC 388-76-10330, WAC 388-76-10335, WAC 388-76-10390, WAC 388-76-10395, WAC 388-76-10545",

    description:
      "Confirm that residents are admitted only after appropriate assessment and when the home can safely meet their assessed needs.",

    icon: "ti-user-check",

    questions: [
      {
        key: "admissionsOccurred",
        prompt:
          "Have any residents been admitted since the previous compliance submission?",
        type: "boolean",
        required: true,
      },

      {
        key: "requiredAssessmentTopicsComplete",
        prompt:
          "For every admission, did the resident assessment contain all required assessment information, or document attempts to obtain any information that could not be obtained?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "staffKnowResidentNeeds",
        prompt:
          "Were the home's staff knowledgeable about the assessed needs and preferences of every newly admitted resident?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "homeCanMeetAssessedNeeds",
        prompt:
          "At admission, could the home safely and appropriately meet every newly admitted resident's assessed needs and preferences?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "qualifiedStaffAvailable",
        prompt:
          "Were sufficient qualified staff available to meet the assessed needs of every newly admitted resident?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "reasonableAccommodationsAvailable",
        prompt:
          "Where reasonable accommodations were required, could the home provide them?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "physicalPlantAndEquipmentReady",
        prompt:
          "Did the home have the physical plant features and equipment needed to safely meet every newly admitted resident's assessed needs (e.g. accessibility, medical equipment)?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "existingResidentsNotCompromised",
        prompt:
          "Did each admission preserve the home's ability to safely meet the needs of existing residents?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "safeEvacuationMaintained",
        prompt:
          "After each admission, could all people in the home still be safely evacuated during an emergency?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "admissionsNonDiscriminatory",
        prompt:
          "Were admission decisions made in accordance with applicable nondiscrimination requirements?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "emergencyAdmissionsOccurred",
        prompt:
          "Were any residents admitted without a completed pre-admission assessment because of a true emergency?",
        type: "boolean",
        required: true,
        showIf: { key: "admissionsOccurred", equals: true },
      },

      {
        key: "emergencyCriteriaVerified",
        prompt:
          "For every emergency admission, was the true emergency verified and documented?",
        type: "boolean",
        required: true,
        showIf: { key: "emergencyAdmissionsOccurred", equals: true },
      },

      {
        key: "emergencyRequirementsMet",
        prompt:
          "For every emergency admission, were the applicable assessment, preliminary service plan, case-manager approval, and approval-documentation requirements completed?",
        type: "boolean",
        required: true,
        showIf: { key: "emergencyAdmissionsOccurred", equals: true },
      },
    ],
  },

  WA_AFH_009_ASSESSOR_QUALIFICATIONS: {
    label: "Assessor Qualifications",
    reference: "WAC 388-76-10150, WAC 388-76-10345",
    description:
      "Confirm that resident assessments are performed only by qualified assessors or authorized department case managers.",
    icon: "ti-certificate",
    questions: [
      {
        key: "assessmentsOccurred",
        prompt:
          "Have any resident assessments (pre-admission, initial, or updated) been performed since the previous compliance submission?",
        type: "boolean",
        required: true,
      },

      {
        key: "departmentCaseManagerAssessmentsOccurred",
        prompt:
          "Were any of these assessments performed by an authorized department case manager for a resident whose care and services are paid for by the department?",
        type: "boolean",
        required: true,
        showIf: { key: "assessmentsOccurred", equals: true },
      },

      {
        key: "caseManagerAuthorizationVerified",
        prompt:
          "For every assessment performed by a department case manager, was the case manager's authorization confirmed?",
        type: "boolean",
        required: true,
        showIf: {
          key: "departmentCaseManagerAssessmentsOccurred",
          equals: true,
        },
      },

      {
        key: "assessorExperienceVerified",
        prompt:
          "For every other assessor who qualifies by degree or nursing license, was the required experience confirmed and documented (master's degree: 2 years, bachelor's degree: 3 years of social service experience with adults who have functional or cognitive disabilities; nursing license: 3 years of clinical nursing experience)?",
        type: "boolean",
        required: true,
        showIf: { key: "assessmentsOccurred", equals: true },
      },

      {
        key: "assessorDegreeFieldVerified",
        prompt:
          "For every assessor who qualifies by degree, was the degree confirmed to be in social services, human services, behavioral sciences, or an allied field?",
        type: "boolean",
        required: true,
        showIf: { key: "assessmentsOccurred", equals: true },
      },

      {
        key: "assessorBackgroundChecksCurrent",
        prompt:
          "For every assessor other than a licensed physician, did the home have a current criminal history background check showing no disqualifying convictions or pending charges and none of the negative actions listed in WAC 388-76-10180?",
        type: "boolean",
        required: true,
        showIf: { key: "assessmentsOccurred", equals: true },
      },
    ],
  },

  WA_AFH_006_RESIDENT_MANAGER_QUALIFICATIONS: {
    label: "Resident Manager Qualifications",
    reference: "WAC 388-76-10145; WAC 388-76-10129; WAC 388-76-10180",
    description:
      "Confirm that the resident manager meets the minimum qualifications required to manage the home.",
    icon: "ti-user-shield",
    questions: [
      {
        key: "hasResidentManager",
        prompt: "Does the home employ or contract with a resident manager?",
        type: "boolean",
        required: true,
      },

      {
        key: "managerAgeVerified",
        prompt:
          "Have you confirmed that every resident manager is at least 21 years old?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerEducationVerified",
        prompt:
          "Do you have documentation that every resident manager has a United States high school diploma or an accepted equivalent?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerCharacterVerified",
        prompt:
          "Have you confirmed that every resident manager has good moral and responsible character and reputation?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerEnglishLiteracyVerified",
        prompt:
          "Have you confirmed that every resident manager is literate and able to communicate in English?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerAdministrativeAbilityVerified",
        prompt:
          "Have you confirmed that every resident manager has the management and administrative ability to carry out the requirements of the adult family home rules?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerCaregivingExperienceVerified",
        prompt:
          "For every resident manager who does not hold a qualifying professional license (physician, osteopathic physician, physician assistant, registered nurse, advanced practice nurse, or licensed practical nurse), do you have credible evidence of at least 1,000 hours of successful direct caregiving experience to vulnerable adults in the previous 60 months?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerContinuingEducationCurrent",
        prompt:
          "Is every resident manager current on department-approved continuing education?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerProvidesSpecialCare",
        prompt:
          "Does any resident manager provide special care services (for example dementia, mental illness, or developmental disabilities care) to a resident?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerSpecialCareTrainingComplete",
        prompt:
          "Did every resident manager providing special care services complete department-approved (or equivalent) special care training before providing those services?",
        type: "boolean",
        required: true,
        showIf: { key: "managerProvidesSpecialCare", equals: true },
      },

      {
        key: "managerBackgroundChecksCurrent",
        prompt:
          "Has every resident manager been cleared by a department background check, with no disqualifying convictions or pending charges and none of the negative actions listed in WAC 388-76-10180?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },

      {
        key: "managerLiveInRequirementMet",
        prompt:
          "Does the resident manager live in the home, or does the home have 24-hour staffing coverage with a staff person who can make needed decisions always present?",
        type: "boolean",
        required: true,
        showIf: { key: "hasResidentManager", equals: true },
      },
    ],
  },

  WA_AFH_056_NURSE_DELEGATION: {
    label: "Nurse Delegation",
    reference:
      "WAC 246-840-930; WAC 246-841A-405; WAC 246-840-910 through 246-840-970",
    description:
      "Confirm that nursing tasks are delegated only by registered nurses to qualified caregivers, with the required consent, instructions, and supervision.",
    icon: "ti-stethoscope",

    questions: [
      {
        key: "nurseDelegationInUse",
        prompt:
          "Do any caregivers in the home currently perform nursing tasks delegated by a registered nurse?",
        type: "boolean",
        required: true,
      },

      {
        key: "delegatorLicenseActive",
        prompt:
          "Does every delegating registered nurse hold an active Washington registered nurse license?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "delegatorAssessmentsDocumented",
        prompt:
          "For every delegation, did the registered nurse assess the resident, determine the resident's condition is stable and predictable, and document the rationale for delegating in the resident's record?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "caregiverCredentialsUnrestricted",
        prompt:
          "Is every caregiver performing delegated tasks currently registered or certified in Washington as a nursing assistant or home care aide, without restriction?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "caregiversWillingAndAble",
        prompt:
          "Is every caregiver performing delegated tasks willing and able to perform them without direct nurse supervision, and do they accept responsibility for their actions?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "noProhibitedTasksDelegated",
        prompt:
          "Is it true that no injections other than insulin have been delegated to any caregiver?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "insulinDelegated",
        prompt:
          "Has any caregiver been delegated to give insulin injections to a resident?",
        type: "boolean",
        required: true,
        showIf: { key: "nurseDelegationInUse", equals: true },
      },

      {
        key: "insulinSupervisionScheduleMet",
        prompt:
          "For each insulin delegation, did the registered nurse supervise and evaluate the caregiver at least every two weeks for the first four weeks, and at least every 90 days after that?",
        type: "boolean",
        required: true,
        showIf: { key: "insulinDelegated", equals: true },
      },

      {
        key: "diabetesTrainingVerified",
        prompt:
          "Has every caregiver giving insulin completed the nurse delegation special focus on diabetes training?",
        type: "boolean",
        required: true,
        showIf: { key: "insulinDelegated", equals: true },
      },
    ],
  },
  WA_AFH_005_PROVIDER_ENTITY_REPRESENTATIVE_QUALIFICATIONS: {
    label: "Provider & Entity Representative Qualifications",

    reference:
      "WAC 388-76-10130; WAC 388-76-10064; WAC 388-76-10146; WAC 388-76-10145",

    description:
      "Confirm that the provider and entity representative meet the minimum qualifications to operate the home.",

    icon: "ti-briefcase",

    questions: [
      {
        key: "hasProviderOrEntityRepresentative",
        prompt:
          "Does the home have a licensed provider or an entity representative who is an individual person?",
        type: "boolean",
        required: true,
      },

      {
        key: "leaderAgeVerified",
        prompt:
          "Have you confirmed that the provider and entity representative are each at least 21 years old?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderEducationVerified",
        prompt:
          "Do you have documentation that the provider and entity representative each have a United States high school diploma or an accepted equivalent?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderCharacterVerified",
        prompt:
          "Have you confirmed that the provider and entity representative each have good moral and responsible character and reputation?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderEnglishLiteracyVerified",
        prompt:
          "Have you confirmed that the provider and entity representative are each literate and able to communicate in English?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderLanguageAccessVerified",
        prompt:
          "Does the home have a way to communicate with each resident in their primary language (for example a qualified person on site or a language line), and is someone on staff always available who speaks and understands English well enough to respond to emergencies and carry out care plans?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderAdministrativeAbilityVerified",
        prompt:
          "Have you confirmed that the provider and entity representative can carry out the management and administrative requirements of the adult family home rules?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderCaregivingExperienceVerified",
        prompt:
          "For anyone without a qualifying professional license (physician, osteopathic physician, physician assistant, osteopathic physician assistant, registered nurse, advanced registered nurse practitioner, or licensed practical nurse), do you have evidence of at least 1,000 hours of successful direct care experience with vulnerable adults in the previous 60 months?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "administrationClassExemptionApplies",
        prompt:
          "If the 48-hour adult family home administration class certificate is not on file for the provider or entity representative, does an exemption apply (a change of ownership where they already take part in a licensed home, an additional home where the class was already taken, or a department waiver)?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderContinuingEducationCurrent",
        prompt:
          "Are the provider and entity representative each current on continuing education?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderProvidesSpecialCare",
        prompt:
          "Does the home serve any residents with specialty care needs (developmental disability, mental illness, or dementia)?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderSpecialCareTrainingComplete",
        prompt:
          "Have the provider, entity representative, resident manager and staff completed the specialty care training required for those residents?",
        type: "boolean",
        required: true,
        showIf: { key: "leaderProvidesSpecialCare", equals: true },
      },

      {
        key: "leaderTuberculosisScreeningVerified",
        prompt:
          "Has the tuberculosis screening needed to establish their status been completed for the provider and entity representative?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },

      {
        key: "leaderBackgroundChecksCurrent",
        prompt:
          "Has the provider and entity representative each been cleared by a background check, with no disqualifying convictions or pending charges and none of the negative actions listed in WAC 388-76-10180?",
        type: "boolean",
        required: true,
        showIf: { key: "hasProviderOrEntityRepresentative", equals: true },
      },
    ],
  },
  WA_AFH_021_QUALIFIED_CAREGIVER_AVAILABILITY: {
    label: "Qualified Caregiver Availability",
    reference: "WAC 388-76-10200; WAC 388-76-10195; Chapter 388-112A WAC",
    description:
      "Confirm a qualified caregiver was in the home whenever residents were present, any unattended-resident exceptions were properly approved, and on-call and management contacts were available.",
    icon: "ti-user-check",
    questions: [
      {
        key: "qualifiedCaregiverAlwaysPresent",
        prompt:
          "Since the last check, was at least one qualified caregiver (someone who has completed orientation and basic training) in the home at all times when one or more residents were in the home?",
        type: "boolean",
        required: true,
      },
      {
        key: "gapsLimitedToApprovedUnattended",
        prompt:
          "Were the times without a qualified caregiver limited to periods when every resident in the home had been approved to be left unattended?",
        type: "boolean",
        showIf: { key: "qualifiedCaregiverAlwaysPresent", equals: false },
        required: true,
      },
      {
        key: "unattendedSafetyAssessmentDone",
        prompt:
          "For each resident left unattended, was an assessment completed beforehand showing the resident can be left alone safely, based on their strengths and needs?",
        type: "boolean",
        showIf: { key: "gapsLimitedToApprovedUnattended", equals: true },
        required: true,
      },
      {
        key: "unattendedCarePlanTimeLimited",
        prompt:
          "Does each of those residents' negotiated care plan state a limited, specific amount of time they are safe to be left unattended?",
        type: "boolean",
        showIf: { key: "gapsLimitedToApprovedUnattended", equals: true },
        required: true,
      },
      {
        key: "unattendedResidentConsented",
        prompt:
          "Did each of those residents consent to the plan to be left unattended?",
        type: "boolean",
        showIf: { key: "gapsLimitedToApprovedUnattended", equals: true },
        required: true,
      },
      {
        key: "unattendedCanReachStaff",
        prompt:
          "Can each resident who is left unattended contact a responsible staff person at all times?",
        type: "boolean",
        required: true,
        showIf: { key: "unattendedResidentConsented", equals: true },
      },
      {
        key: "unattendedEmergencyKnowledge",
        prompt:
          "Does each resident who is left unattended know what to do in an emergency, and can they act on it, such as leaving the home or calling 911?",
        type: "boolean",
        required: true,
        showIf: { key: "unattendedResidentConsented", equals: true },
      },
      {
        key: "gapDetails",
        prompt:
          "Briefly describe when residents were without a qualified caregiver and what has been done to prevent it.",
        type: "text",
        showIf: { key: "qualifiedCaregiverAlwaysPresent", equals: false },
        required: true,
      },
      {
        key: "residentsTakenOnOutings",
        prompt:
          "Since the last check, were any residents taken out of the home (for example appointments or outings) whose care plan does not allow them to be left unattended?",
        type: "boolean",
        required: true,
      },
      {
        key: "staffAvailableDuringOutings",
        prompt:
          "Was staff readily available to meet those residents' needs throughout each outing?",
        type: "boolean",
        showIf: { key: "residentsTakenOnOutings", equals: true },
        required: true,
      },
      {
        key: "caregiverOrientationCompleted",
        prompt:
          "Has every caregiver you count as qualified completed the required orientation (in addition to basic training)?",
        type: "boolean",
        required: true,
      },
      {
        key: "onCallDesigneeReachable",
        prompt:
          "Is an experienced staff member designated to respond on behalf of the home by phone or pager at all times, including when no residents are home?",
        type: "boolean",
        required: true,
      },
      {
        key: "onCallNumberProvided",
        prompt:
          "Has the on-call telephone or pager number been given to each resident, their representatives, and authorized state staff?",
        type: "boolean",
        required: true,
        showIf: { key: "onCallDesigneeReachable", equals: true },
      },
      {
        key: "managementReadilyAvailable",
        prompt:
          "Is the provider, entity representative, or resident manager readily available to each resident?",
        type: "boolean",
        required: true,
      },
      {
        key: "staffingSufficientForResidentNeeds",
        prompt:
          "Whenever residents were in the home, were enough staff available to meet the needs of each resident?",
        type: "boolean",
        required: true,
      },
      {
        key: "hasLiveInCaregiver",
        prompt:
          "Does a live-in provider, entity representative, resident manager or caregiver cover times when no caregiver is clocked in (for example overnight)?",
        type: "boolean",
        required: true,
      },

      {
        key: "liveInPresentDuringGaps",
        prompt:
          "Was that live-in person in the home at all times when no caregiver was clocked in?",
        type: "boolean",
        required: true,
        showIf: { key: "hasLiveInCaregiver", equals: true },
      },

      {
        key: "allCaregiversClockInOut",
        prompt:
          "Does every caregiver who works in the home clock in and out in OCare?",
        type: "boolean",
        required: true,
        showIf: { key: "hasLiveInCaregiver", equals: false },
      },
    ],
  },
  WA_AFH_086_RESIDENT_SUPERVISION: {
    label: "Resident Supervision",

    reference: "WAC 388-76-10195; WAC 388-76-10200",

    description:
      "Confirm that each resident receives the supervision their care plan calls for, day and night, in a way that respects their rights and keeps them safe.",

    icon: "ti-eye-check",

    questions: [
      {
        key: "ncpStatesSupervisionLevel",
        prompt:
          "Does every resident's negotiated care plan state how closely staff must supervise the resident, including how often any checks happen?",
        type: "boolean",
        required: true,
      },

      {
        key: "supervisionBasedOnAssessment",
        prompt:
          "Is each resident's supervision based on their current assessment of physical, cognitive and behavioral needs, and reviewed when their needs change?",
        type: "boolean",
        required: true,
      },

      {
        key: "nighttimeRoutineDocumented",
        prompt:
          "Does every care plan state the resident's nighttime care routine and monitoring (for example repositioning, toileting or incontinence care, and night checks)?",
        type: "boolean",
        required: true,
      },

      {
        key: "nightStaffingMatchesNeeds",
        prompt:
          "Does the home's night staffing (awake or sleeping staff) match the combined needs of all residents, so every resident's needs can be met overnight?",
        type: "boolean",
        required: true,
      },

      {
        key: "nightStaffCanRespond",
        prompt:
          "Can the staff on duty overnight respond immediately to call systems, alarms and residents in distress?",
        type: "boolean",
        required: true,
      },

      {
        key: "nightChecksRequired",
        prompt: "Does any resident's care plan require scheduled night checks?",
        type: "boolean",
        required: true,
      },

      {
        key: "nightChecksLogged",
        prompt:
          "Do you keep a log showing that the nighttime checks required by the care plans were done?",
        type: "boolean",
        required: true,
        showIf: { key: "nightChecksRequired", equals: true },
      },

      {
        key: "shiftHandoffCommunicatesRisks",
        prompt:
          "Do shift handoffs tell oncoming staff about changes in a resident's supervision needs and any heightened fall or elopement risk?",
        type: "boolean",
        required: true,
      },

      {
        key: "supervisionIncidentsOccurred",
        prompt:
          "Since the previous submission, did any resident fall, attempt to wander, or show unsafe behavior?",
        type: "boolean",
        required: true,
      },

      {
        key: "supervisionReviewedAfterIncidents",
        prompt:
          "After each of those events, did you promptly review the resident's supervision and update their care plan if needed?",
        type: "boolean",
        required: true,
        showIf: { key: "supervisionIncidentsOccurred", equals: true },
      },

      {
        key: "roomMonitoringInUse",
        prompt:
          "Are audio or video monitors used in any resident's private room?",
        type: "boolean",
        required: true,
      },

      {
        key: "roomMonitoringConsentOnFile",
        prompt:
          "Is written consent from the resident or their representative on file for every in-room monitor?",
        type: "boolean",
        required: true,
        showIf: { key: "roomMonitoringInUse", equals: true },
      },

      {
        key: "exitAlarmsInUse",
        prompt: "Does the home use door alarms or delayed-exit systems?",
        type: "boolean",
        required: true,
      },

      {
        key: "alarmsUsedAsAlertsOnly",
        prompt:
          "Are those alarms and systems used only to alert staff, with residents who are at risk of leaving still supervised by staff?",
        type: "boolean",
        required: true,
        showIf: { key: "exitAlarmsInUse", equals: true },
      },

      {
        key: "noUnauthorizedRestraint",
        prompt:
          "Is it true that no resident is physically restrained or confined (for example locked in their room or the home), except where the law specifically allows it?",
        type: "boolean",
        required: true,
      },

      {
        key: "evacuationAlignedWithSupervision",
        prompt:
          "Do your supervision and night staffing arrangements allow all residents to be evacuated within the required timeframes?",
        type: "boolean",
        required: true,
      },
    ],
  },
  WA_AFH_013_CONDITIONAL_PROVISIONAL_HIRE_CONTROLS: {
    label: "Conditional & Provisional Hire Controls",
    reference:
      "WAC 388-76-10175; WAC 388-76-10176; WAC 388-76-10161; WAC 388-76-10180",
    description:
      "Confirm that staff hired before their background check results were back were hired under the permitted controls: timely checks, disclosure, supervision, and the 120-day fingerprint limit.",
    icon: "ti-user-search",
    questions: [
      {
        key: "hadConditionalHires",
        prompt:
          "Since the last check, did the home conditionally employ anyone (directly or by contract) while waiting for their Washington state name and date of birth background check result?",
        type: "boolean",
        required: true,
      },
      {
        key: "conditionalCheckSubmittedWithinOneWorkingDay",
        prompt:
          "For every conditional hire, was the Washington state name and date of birth background check submitted no later than one working day after they started?",
        type: "boolean",
        showIf: { key: "hadConditionalHires", equals: true },
        required: true,
      },
      {
        key: "conditionalDisclosureSignedAndDenied",
        prompt:
          "Did every conditional hire sign a disclosure statement in which they deny having a disqualifying criminal conviction, a pending charge for a disqualifying crime, or a disqualifying negative action?",
        type: "boolean",
        showIf: { key: "hadConditionalHires", equals: true },
        required: true,
      },
      {
        key: "conditionalNoUnsupervisedAccess",
        prompt:
          "Is it true that no conditional hire had unsupervised access to any resident before their background check cleared?",
        type: "boolean",
        showIf: { key: "hadConditionalHires", equals: true },
        required: true,
      },
      {
        key: "conditionalDirectSupervision",
        prompt:
          "Was every conditional hire under direct supervision whenever they worked with residents?",
        type: "boolean",
        showIf: { key: "hadConditionalHires", equals: true },
        required: true,
      },
      {
        key: "conditionalTrainingAndCompetence",
        prompt:
          "Was every conditional hire competent and trained for their assigned tasks, and did they meet the staff training requirements under chapter 388-112A WAC?",
        type: "boolean",
        showIf: { key: "hadConditionalHires", equals: true },
        required: true,
      },
      {
        key: "conditionalGapDetails",
        prompt:
          "Describe which conditional hire(s) had unsupervised access and what has been done to correct it.",
        type: "text",
        showIf: { key: "conditionalNoUnsupervisedAccess", equals: false },
        required: true,
      },
      {
        key: "hadProvisionalHires",
        prompt:
          "Is anyone currently working, or did anyone work since the last check, as a provisional hire (hired after January 7, 2012, with national fingerprint background check results still pending)?",
        type: "boolean",
        required: true,
      },
      {
        key: "provisionalNotDisqualifiedByNameDob",
        prompt:
          "Was every provisional hire confirmed as not disqualified by the Washington state name and date of birth background check?",
        type: "boolean",
        showIf: { key: "hadProvisionalHires", equals: true },
        required: true,
      },
      {
        key: "provisionalUnsupervisedAccessOnlyAfterClear",
        prompt:
          "Did provisional hires get unsupervised access to residents only after the state name and date of birth check cleared them?",
        type: "boolean",
        showIf: { key: "hadProvisionalHires", equals: true },
        required: true,
      },
      {
        key: "provisionalFingerprintWithin120Days",
        prompt:
          "Has every provisional hire either received their national fingerprint background check results or is still within their first 120 days of hire?",
        type: "boolean",
        showIf: { key: "hadProvisionalHires", equals: true },
        required: true,
      },
      {
        key: "provisionalGapDetails",
        prompt:
          "Describe which provisional hire(s) passed 120 days without fingerprint results and what has been done (for example, removing their unsupervised access).",
        type: "text",
        showIf: { key: "provisionalFingerprintWithin120Days", equals: false },
        required: true,
      },
    ],
  },
  WA_AFH_093_CORRECTIVE_ACTION_COMPLETION: {
    label: "Corrective Action Completion",
    reference: "WAC 388-76-10930",
    description:
      "Confirm that deficiencies cited by the department were attested to on time, corrected by the approved dates, and that correction is being maintained.",
    icon: "ti-checklist",
    questions: [
      {
        key: "hadCitedDeficiencies",
        prompt:
          "Since the last check, did the home receive an inspection report with cited deficiencies, or does it still have cited deficiencies from an earlier report that are not yet corrected?",
        type: "boolean",
        required: true,
      },
      {
        key: "attestationsReturnedWithin10Days",
        prompt:
          "For every inspection report received, did the home return the report to the department, with a completed attestation of correction for each cited deficiency, within 10 calendar days of receiving it?",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
      {
        key: "attestationsSignedAndDated",
        prompt:
          "Did the home sign and date each attestation, confirming it has corrected or will correct the deficiency and will maintain the correction?",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
      {
        key: "correctionDatesApproved",
        prompt:
          "Does every cited deficiency have a correction date that the department approved?",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
      {
        key: "allCorrectedByApprovedDate",
        prompt:
          "Has every cited deficiency been corrected by its department-approved date? (Answer yes if the date has not passed yet.)",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
      {
        key: "overdueDetails",
        prompt:
          "Which deficiencies are past their approved date, and what is being done to correct them?",
        type: "text",
        showIf: { key: "allCorrectedByApprovedDate", equals: false },
        required: true,
      },
      {
        key: "correctionMaintained",
        prompt:
          "Is the correction of each cited deficiency being maintained, with no recurrence?",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
      {
        key: "planAndMaintenanceDocumentationAvailable",
        prompt:
          "Can the home show the department, on request, a plan of correction and proof of maintaining correction for each cited deficiency?",
        type: "boolean",
        showIf: { key: "hadCitedDeficiencies", equals: true },
        required: true,
      },
    ],
  },
  WA_AFH_064_RESIDENT_PROPERTY_INVENTORY: {
    label: "Resident Property Inventory",
    reference: "WAC 388-76-10320(10); DSHS 02-516",
    description:
      "Confirm each resident's record has a current, dated, and signed inventory of their personal belongings.",
    icon: "ti-luggage",
    questions: [
      {
        key: "everyResidentHasInventory",
        prompt:
          "Does every resident's record contain a personal belongings inventory?",
        type: "boolean",
        required: true,
      },
      {
        key: "inventoriesSignedAndDated",
        prompt:
          "Is every inventory dated and signed by both the home and the resident (or, where the resident cannot sign, their guardian or authorized agent)?",
        type: "boolean",
        required: true,
      },
      {
        key: "inventoriesCurrent",
        prompt:
          "Is every inventory current, updated to reflect the resident's belongings now (items added, removed, or lost since it was completed)?",
        type: "boolean",
        required: true,
      },
      {
        key: "hadAdmissions",
        prompt:
          "Since the last check, were any residents admitted to the home?",
        type: "boolean",
        required: true,
      },
      {
        key: "inventoryCompletedAtAdmission",
        prompt:
          "For every resident admitted since the last check, was the belongings inventory completed when they were admitted?",
        type: "boolean",
        showIf: { key: "hadAdmissions", equals: true },
        required: true,
      },
      {
        key: "gapDetails",
        prompt:
          "Which residents are missing an inventory, and when will it be completed?",
        type: "text",
        showIf: { key: "everyResidentHasInventory", equals: false },
        required: true,
      },
    ],
  },
};
